import React, { FunctionComponent, useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Nav, Tab } from 'react-bootstrap';
import { formatPhoneNumber } from '../../../../common/helpers/phoneHelper';
import { IAppState } from '../../../../common/models/store/IAppState';
import { IContact } from '../../../../common/models/contact/IContact';
import { IBindingCallback1, IBindingAction } from '../../../../common/models/callback';
import { fetchContactRoutine, setRoleRoutine } from '../../routines';
import { ContactType } from '../../../../common/enums/ContactType';
import CandidatePane from '../CandidatePane';
import ProspectPane from '../ProspectPane';
import ReferencePane from '../ReferencePane';
import { hideContactProfileRoutine } from '../../../Dashboard/routines';
import Icon from '../../../../components/Icon';
import LoaderWrapper from '../../../../components/LoaderWrapper';
import { ISelectedRole } from '../../../../common/models/contact/ISelectedRole';
import { getProfileIcon } from './utils';
import styles from './styles.module.scss';

interface IContactProfileProps {
  contactId: string;
  role: ContactType;
  contact: IContact;
  loading: boolean;
  setRole: IBindingCallback1<ISelectedRole>;
  fetchContact: IBindingCallback1<string>;
  hideContact: IBindingAction;
}

const ContactProfile: FunctionComponent<IContactProfileProps> = ({
  contactId,
  contact,
  role,
  loading,
  setRole,
  fetchContact
}) => {
  useEffect(() => {
    if (contactId) {
      fetchContact(contactId);
    }
  }, [contactId]);

  const ContactInfo = ({ firstName, lastName, phoneNumber, email }: IContact) => (
    <div className={styles.contactInfo}>
      <div className={styles.contactName}>{`${firstName} ${lastName}`}</div>
      {email && (
        <div className={styles.infoBlock}>
          <Icon icon="email" />
          {email}
        </div>
      )}
      {phoneNumber && (
        <div className={styles.infoBlock}>
          <Icon icon="phone" />
          {formatPhoneNumber(phoneNumber)}
        </div>
      )}
    </div>
  );

  const Pane = useCallback(() => {
    switch (role) {
      case ContactType.Candidate:
        return <CandidatePane />;
      case ContactType.Prospect:
        return <ProspectPane />;
      case ContactType.Reference:
        return <ReferencePane />;
      default:
        return <></>;
    }
  }, [role]);

  const onChangeTab = (key: ContactType) => {
    if (key !== role) {
      setRole({
        contactId,
        candidateId: key === ContactType.Candidate ? contact.candidates[0].id : undefined,
        role: key });
    }
  };

  const getRoleTab = (tabKey: ContactType) => (
    <Nav.Item className="tab">
      <Nav.Link
        className="tab-link t-14"
        onSelect={key => onChangeTab(key as ContactType)}
        eventKey={tabKey}
      >
        <Icon icon={getProfileIcon(tabKey)} />
        <FormattedMessage id={`${tabKey}Profile`} defaultMessage={`${tabKey} Profile`} />
      </Nav.Link>
    </Nav.Item>
  );

  return (
    <div className="profile-modal">
      <LoaderWrapper loading={loading || !contact}>
        <Tab.Container activeKey={role}>
          <div className={styles.profileHeader}>
            <ContactInfo {...contact} />
            <Nav>
              {Boolean(contact?.candidates.length) && getRoleTab(ContactType.Candidate)}
              {contact?.isReference && getRoleTab(ContactType.Reference)}
              {Boolean(contact?.prospect) && getRoleTab(ContactType.Prospect)}
            </Nav>
          </div>
          <div className={`profile-body ${styles.contactProfile}`}><Pane /></div>
        </Tab.Container>
      </LoaderWrapper>
    </div>
  );
};

const mapStateToProps = ({
  contactProfile: {
    contact: {
      contact,
      contactId,
      role,
      loading
    }
  }
}: IAppState) => ({ contact, role, contactId, loading });

const mapDispachToProps = {
  fetchContact: fetchContactRoutine,
  hideContact: hideContactProfileRoutine,
  setRole: setRoleRoutine
};

export default connect(mapStateToProps, mapDispachToProps)(ContactProfile);
